import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Copy, Download, Loader2, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import CountdownTimer from "@/components/CountdownTimer";
import DecodingKey from "@/components/DecodingKey";
import FlexCard from "@/components/FlexCard";

const TIER_LABELS = { day: "1 DAY", week: "1 WEEK", "15day": "15 DAYS", month: "1 MONTH", "3month": "3 MONTHS", lifetime: "LIFETIME", instant: "INSTANT" };

function vaultStatus(item, expired) {
  if (expired) return ["EXPIRED", "border-red-400/40 text-red-300"];
  if (item.key_status === "delivered") return ["ACTIVE", "border-emerald-300/30 text-emerald-200"];
  if (item.key_status === "partial") return ["PARTIAL", "border-amber-300/30 text-amber-200"];
  return ["KEY QUEUED", "border-amber-300/30 text-amber-200"];
}

export default function KeyVaultCard({ order, item, index }) {
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [showNfa, setShowNfa] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const slug = item.slug || order.slug;
  const expiresAt = item.expires_at || order.expires_at;
  const expired = expiresAt && expiresAt !== "lifetime" && new Date(expiresAt).getTime() <= now;
  const [status, statusCls] = vaultStatus(item, expired);
  const keys = (item.keys?.length ? item.keys : (index === 0 ? order.keys || [] : [])).filter(Boolean);
  const pending = keys.length === 0;
  const cardCls = expired
    ? "border-red-500/30 bg-red-950/10 hover:border-red-500/50"
    : pending
      ? "border-amber-500/25 bg-amber-950/10 hover:border-amber-400/45"
      : "border-white/10 bg-black/90 hover:border-white/30";

  const copy = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("License key copied");
  };

  const renew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.session_id}/renew`, { item_index: index, method: "auto" });
      window.location.href = data.checkout_url || `/pay/${data.session_id}`;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Renewal unavailable");
      setBusy(false);
    }
  };

  return (
    <article className={`relative rounded-2xl border p-5 sm:p-6 transition-colors ${cardCls}`} data-testid={`key-vault-card-${slug}-${index}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/product/${slug}`} className="font-display text-xl sm:text-2xl uppercase leading-none hover:text-white/75 transition-colors">
              {item.product_name || order.product_name}
            </Link>
            <span className="rounded-full border border-white/15 px-2 py-1 font-mono text-[9px] tracking-[0.18em] text-white/50">
              {(item.category || order.category || "PRODUCT").toUpperCase()}
            </span>
            <span className="rounded-full border border-white/15 px-2 py-1 font-mono text-[9px] tracking-[0.18em] text-white/60">
              {TIER_LABELS[item.tier] || item.tier}
            </span>
          </div>
          <div className="mt-2 font-mono text-[9px] tracking-[0.18em] text-white/30">
            ORDER {order.session_id.slice(0, 8).toUpperCase()} · ${(order.amount || 0).toFixed(2)}
          </div>
        </div>
        <span className={`rounded-full border px-2.5 py-1 font-mono text-[10px] tracking-[0.18em] ${statusCls}`}>{status}</span>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
        <div>
          <div className="font-mono text-[9px] tracking-[0.22em] text-white/35">LICENSE KEY{keys.length > 1 ? "S" : ""}</div>
          {pending ? (
            <div className="mt-3 rounded-xl border border-amber-300/20 bg-amber-300/[0.04] px-4 py-4 font-mono text-[10px] tracking-[0.16em] text-amber-100/70">
              <span className="pulse-dot mr-2 inline-block h-1.5 w-1.5 rounded-full bg-amber-200" />
              KEY DISPATCH QUEUED — WATCH YOUR EMAIL
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {keys.map((key, keyIndex) => (
                <div key={keyIndex} className="flex items-center gap-2">
                  <DecodingKey value={key} tid={`vault-key-${slug}-${index}-${keyIndex}`}
                    className="min-w-0 flex-1 break-all rounded-full border border-white/10 bg-white/[0.06] px-3 py-2 font-mono text-xs tracking-[0.08em] text-white" />
                  <button onClick={() => copy(key)} data-testid={`copy-key-${slug}-${index}${keyIndex ? `-${keyIndex}` : ""}`}
                    className="rounded-full border border-white/10 p-2 text-white/50 transition-colors hover:border-white/40 hover:text-white" title="Copy key">
                    <Copy size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {item.key_status === "partial" && (
            <div className="mt-2 font-mono text-[9px] tracking-[0.15em] text-amber-100/50">
              // {keys.length} OF {item.quantity || 1} DELIVERED — REST COMING BY EMAIL
            </div>
          )}
        </div>

        <div className="lg:text-right">
          <div className="font-mono text-[9px] tracking-[0.22em] text-white/35">TIME REMAINING</div>
          <div className="mt-3 lg:flex lg:justify-end">
            <CountdownTimer endsAt={expiresAt} now={now} tid={`expiry-countdown-${slug}-${index}`} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2 lg:justify-end">
            {item.loader_url && (
              <a href={item.loader_url} target="_blank" rel="noreferrer" data-testid={`loader-download-${slug}-${index}`}
                className="btn-ghost inline-flex items-center gap-2 px-4 py-2.5 text-[10px]">
                <Download size={13} /> LOADER
              </a>
            )}
            {item.tutorial_url && (
              <a href={item.tutorial_url} target="_blank" rel="noreferrer" data-testid={`setup-guide-${slug}-${index}`}
                className="btn-ghost inline-flex items-center gap-2 px-4 py-2.5 text-[10px]">
                <BookOpen size={13} /> SETUP
              </a>
            )}
            {item.setup_kind === "nfa" && (
              <button onClick={() => setShowNfa(!showNfa)} data-testid={`nfa-guide-${slug}-${index}`}
                className="btn-ghost inline-flex items-center gap-2 px-4 py-2.5 text-[10px]">
                <BookOpen size={13} /> NFA GUIDE
              </button>
            )}
            {item.can_renew && (
              <button onClick={renew} disabled={busy} data-testid={`renew-item-${slug}-${index}`}
                className="btn-inj inline-flex items-center gap-2 px-4 py-2.5 text-[10px] disabled:opacity-60">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} RENEW
              </button>
            )}
          </div>
        </div>
      </div>

      {showNfa && item.setup_kind === "nfa" && (
        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-4 font-mono text-[11px] tracking-[0.12em] text-white/60" data-testid={`nfa-steps-${slug}-${index}`}>
          RUN THE EXE → PRESS ADD ACCOUNT → EXIT LOADER → OPEN STEAM
        </div>
      )}

      {index === 0 && (
        <div className="mt-5 flex justify-end border-t border-white/[0.07] pt-4">
          <FlexCard order={order} compact buttonTestId={`flex-order-${order.session_id.slice(0, 8)}`} />
        </div>
      )}
    </article>
  );
}
